/**
 * The weekly financial dashboard.
 *
 * The week is Sunday to Saturday. Cash alone also has a two-week view, because
 * the firm runs cash on a two-week cycle; it opens on that one.
 *
 * Read once a week, top to bottom: the three biggest concerns first, then cash,
 * receivables, budget, production and trust. Every figure is read off a module
 * by the API and rendered here as it arrives — **nothing on this page is
 * computed**, for the reason the executive dashboard gives: a page that
 * recalculated even a total would be a second implementation of it.
 *
 * A section the viewer may not see is absent from the payload and so absent
 * here. A figure that cannot be read is said to be unavailable, never zero.
 */
import { Suspense, lazy, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AGING_BUCKETS,
  AGING_BUCKET_META,
  WEEKLY_NOT_BUILT,
  WEEKLY_SECTION_META,
  formatBps,
  type AlertSeverity,
  type RetainerGoalSettings,
  type WeeklyBudget,
  type WeeklyCash,
  type WeeklyCashWindow,
  type WeeklyCollections,
  type WeeklyConcern,
  type WeeklyDashboardPayload,
  type WeeklyProduction,
  type WeeklyRetainers,
  type WeeklySection,
  type WeeklyTrust,
} from '@accounting/shared'
import { ApiError, apiFetch } from '../lib/api'
import { useSession } from '../lib/session'
import { formatCount, formatDate, formatHours, formatMoney, formatRelative } from '../lib/format'
import { Alert } from '../ui/Alert'
import { Badge, type Tone } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, TextInput } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'
import { Progress } from '../ui/Progress'
import { Stat } from '../ui/Stat'
import { Td, Th } from '../ui/Table'
import { ChevronIcon } from '../ui/icons'
import { formatValue } from './Alerts'

/** Recharts stays out of the first bundle; see `ExecutiveDashboard`. */
const CashFlowChart = lazy(() => import('./CashFlowChart'))

export function WeeklyDashboard() {
  const query = useQuery({
    queryKey: ['weekly'],
    queryFn: () => apiFetch<WeeklyDashboardPayload>('/weekly'),
  })

  const data = query.data

  return (
    <>
      <PageHeader
        title="Weekly financial dashboard"
        description={
          data
            ? `Week of Sunday ${formatDate(data.weekStart)} to Saturday ${formatDate(data.weekEnd)}. Calculated ${formatRelative(data.asOf)}.`
            : 'Cash, collections, budget, production and trust for the week, with the biggest concerns first.'
        }
      />

      {query.error && (
        <Alert tone="error" className="mb-4">
          {query.error instanceof ApiError ? query.error.message : 'The dashboard could not be loaded.'}
        </Alert>
      )}

      {query.isPending && <p className="text-sm text-ink-600">Loading the week…</p>}

      {data && (
        <div className="space-y-6">
          {data.concerns && <Concerns concerns={data.concerns} />}
          {data.cash && <Cash cash={data.cash} />}
          <div className="grid gap-6 xl:grid-cols-2">
            {data.collections && <Collections collections={data.collections} />}
            {data.budget && <Budget budget={data.budget} />}
            {data.production && <Production production={data.production} />}
            {data.retainers && <Retainers retainers={data.retainers} />}
            {data.trust && <Trust trust={data.trust} />}
          </div>
          <NotBuilt />
        </div>
      )}
    </>
  )
}

/* ------------------------------------------------------------------------ *
 * Shared pieces
 * ------------------------------------------------------------------------ */

function SectionCard({
  section,
  description,
  children,
}: {
  section: WeeklySection
  description?: ReactNode
  children: ReactNode
}) {
  const meta = WEEKLY_SECTION_META[section]

  return (
    <Card>
      <CardHeader
        title={meta.label}
        description={description}
        actions={
          <Link
            to={meta.drillTo}
            className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-900"
          >
            Open <ChevronIcon className="shrink-0" />
          </Link>
        }
      />
      <CardBody>{children}</CardBody>
    </Card>
  )
}

/**
 * A narrow table for a card column. `TableWrap` sets a minimum width sized for
 * full-page tables, which would make these scroll sideways inside half a card.
 */
function CompactTable({ children }: { children: ReactNode }) {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  )
}

function Grid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{children}</div>
}

/* ------------------------------------------------------------------------ *
 * Concerns
 * ------------------------------------------------------------------------ */

const SEVERITY_TONES: Record<AlertSeverity, Tone> = {
  critical: 'critical',
  warning: 'warning',
  info: 'neutral',
}

function Concerns({ concerns }: { concerns: WeeklyConcern[] }) {
  return (
    <SectionCard
      section="concerns"
      description="The most serious open alerts, ranked by severity and then by how close the rule sits to client money and cash."
    >
      {concerns.length === 0 ? (
        <p className="text-sm text-ink-600">No open alerts. Nothing has crossed a threshold.</p>
      ) : (
        <ol className="grid gap-3 md:grid-cols-3">
          {concerns.map((concern, index) => (
            <li key={concern.id}>
              <Link
                to={concern.link}
                className="block h-full rounded-lg border border-ink-200 px-4 py-3 transition-colors hover:border-brand-300 hover:bg-brand-50/40"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display text-lg font-semibold text-ink-400">{index + 1}</span>
                  <Badge tone={SEVERITY_TONES[concern.severity]}>{concern.severity}</Badge>
                </div>
                <p className="mt-1 font-semibold text-brand-900">{concern.label}</p>
                {concern.entityLabel && (
                  <p className="text-sm text-ink-700">{concern.entityLabel}</p>
                )}
                <p className="mt-2 text-xs text-ink-600">
                  {formatValue(concern.unit, concern.trippedValue)} against a threshold of{' '}
                  {formatValue(concern.unit, concern.thresholdAtTrigger)} · raised{' '}
                  {formatRelative(concern.triggeredAt)}
                </p>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </SectionCard>
  )
}

/* ------------------------------------------------------------------------ *
 * Cash
 * ------------------------------------------------------------------------ */

const PAYMENT_ROWS = 8

type CashView = 'week' | 'twoWeeks'

const CASH_VIEWS: Record<CashView, { label: string; span: string; none: string }> = {
  week: { label: 'This week', span: 'this week', none: 'Nothing else falls due this week.' },
  twoWeeks: { label: 'Two weeks', span: '2 weeks', none: 'Nothing falls due in the next two weeks.' },
}

function Cash({ cash }: { cash: WeeklyCash }) {
  /** Two weeks first: cash runs on a two-week cycle. */
  const [view, setView] = useState<CashView>('twoWeeks')
  const { week, payables, forecast, projectionSources } = cash
  const lookahead: WeeklyCashWindow = view === 'week' ? cash.weekLookahead : cash.lookahead
  const { span } = CASH_VIEWS[view]

  return (
    <SectionCard
      section="cash"
      description={
        view === 'week'
          ? `Balances from QuickBooks, and the rest of the week to Saturday ${formatDate(lookahead.through)} from the cash forecast.`
          : `Balances from QuickBooks, and the next two weeks to ${formatDate(lookahead.through)} from the cash forecast.`
      }
    >
      <div
        role="radiogroup"
        aria-label="Cash window"
        className="mb-4 inline-flex rounded-lg border border-ink-200 p-0.5"
      >
        {(Object.keys(CASH_VIEWS) as CashView[]).map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={view === option}
            onClick={() => setView(option)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              view === option ? 'bg-brand-700 text-white' : 'text-ink-600 hover:text-ink-900'
            }`}
          >
            {CASH_VIEWS[option].label}
          </button>
        ))}
      </div>

      {!cash.available && (
        <Alert tone="warning" className="mb-4" title="Operating cash is unavailable">
          {cash.unavailableReason ?? 'The cash position could not be read.'}
        </Alert>
      )}

      {projectionSources.promises.status === 'unavailable' && (
        <Alert tone="warning" className="mb-4" title="Promises to pay are missing">
          The billing portal could not be read just now, so expected deposits leave out promises to
          pay until it can.
        </Alert>
      )}

      <Grid>
        <Stat
          label="Operating cash"
          value={formatMoney(cash.operatingBalanceMinor, 'Unavailable')}
          tone={cash.operatingBalanceMinor === null ? 'muted' : 'default'}
          hint={cash.balanceAsOf ? `QuickBooks balance as of ${formatDate(cash.balanceAsOf)}` : null}
        />
        <Stat
          label="Trust bank (IOLTA)"
          value={formatMoney(cash.trustBalanceMinor, 'Unavailable')}
          tone={cash.trustBalanceMinor === null ? 'muted' : 'default'}
          hint="Client money. Never part of operating cash."
        />
        <Stat
          label={`Expected deposits, ${span}`}
          value={formatMoney(lookahead.depositsMinor.expected)}
          hint={`Invoices ${formatMoney(lookahead.receiptsMinor.expected)} · ${
            projectionSources.promises.status === 'connected'
              ? `promises ${formatMoney(lookahead.promisesMinor.expected)} (${formatCount(lookahead.promiseCount)})`
              : 'promises not connected'
          } · new clients ${formatMoney(lookahead.newClientsMinor.expected)}. Range ${formatMoney(lookahead.depositsMinor.conservative)} to ${formatMoney(lookahead.depositsMinor.optimistic)}.`}
        />
        <Stat
          label={`Projected IOLTA transfers, ${span}`}
          value={formatMoney(lookahead.trustTransfersMinor)}
          hint="From the latest billing forecast run"
        />
        <Stat
          label={`Bills due, ${span}`}
          value={formatMoney(lookahead.billsMinor)}
          hint={`${formatMoney(payables.overdueMinor)} already overdue`}
          tone={payables.overdueMinor > 0 ? 'warning' : 'default'}
        />
        <Stat
          label={`Payroll and other obligations, ${span}`}
          value={formatMoney(lookahead.obligationsMinor)}
          hint="Recurring obligations entered on Cash flow"
        />
        <Stat
          label="Transferred to operating this week"
          value={formatMoney(week.transfersToOperatingMinor)}
          hint={`${formatCount(week.transferCount)} transfer${week.transferCount === 1 ? '' : 's'} out of trust`}
        />
        <Stat
          label="Vendor bills paid this week"
          value={formatMoney(week.billsPaidMinor)}
          hint={`${formatCount(week.billsPaidCount)} bill${week.billsPaidCount === 1 ? '' : 's'}. Bills only, not card or cheque spend.`}
        />
      </Grid>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <h3 className="mb-1 text-sm font-semibold text-brand-900">90-day forecast</h3>
          <p className="mb-3 text-xs text-ink-600">
            {forecast.firstShortfallDate
              ? `Even the optimistic case falls below zero on ${formatDate(forecast.firstShortfallDate)}.`
              : `No shortfall projected through ${formatDate(forecast.to)}.`}{' '}
            {lookahead.closingExpectedMinor !== null &&
              `Expected balance on ${formatDate(lookahead.through)}: ${formatMoney(lookahead.closingExpectedMinor)}.`}
          </p>
          <Suspense fallback={<p className="text-sm text-ink-600">Loading the chart…</p>}>
            <CashFlowChart forecast={forecast} />
          </Suspense>
        </div>

        <div className="lg:col-span-2">
          <h3 className="mb-3 text-sm font-semibold text-brand-900">Upcoming payments</h3>
          {lookahead.payments.length === 0 ? (
            <p className="text-sm text-ink-600">{CASH_VIEWS[view].none}</p>
          ) : (
            <CompactTable>
                <thead>
                  <tr>
                    <Th>Due</Th>
                    <Th>Payment</Th>
                    <Th className="text-right">Amount</Th>
                  </tr>
                </thead>
                <tbody>
                  {lookahead.payments.slice(0, PAYMENT_ROWS).map((payment, index) => (
                    <tr key={`${payment.label}-${payment.date}-${index}`}>
                      <Td className="whitespace-nowrap">{formatDate(payment.date)}</Td>
                      <Td>{payment.label}</Td>
                      <Td className="text-right tabular-nums">{formatMoney(payment.amountMinor)}</Td>
                    </tr>
                  ))}
                </tbody>
              </CompactTable>
          )}
          {lookahead.payments.length > PAYMENT_ROWS && (
            <p className="mt-2 text-xs text-ink-600">
              {lookahead.payments.length - PAYMENT_ROWS} more on the{' '}
              <Link to="/cash-flow" className="text-brand-700 underline">
                cash flow page
              </Link>
              .
            </p>
          )}
        </div>
      </div>
    </SectionCard>
  )
}

/* ------------------------------------------------------------------------ *
 * Collections
 * ------------------------------------------------------------------------ */

function Collections({ collections }: { collections: WeeklyCollections }) {
  const { commitments } = collections

  return (
    <SectionCard
      section="collections"
      description={`${formatCount(collections.invoiceCount)} open invoices across ${formatCount(collections.clientCount)} clients.`}
    >
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Outstanding" value={formatMoney(collections.outstandingMinor)} />
        <Stat
          label="Overdue"
          value={formatMoney(collections.overdueMinor)}
          tone={collections.overdueMinor > 0 ? 'warning' : 'default'}
          hint={`${formatCount(collections.highRiskCount)} high-risk accounts owe ${formatMoney(collections.highRiskOverdueMinor)} past due`}
        />
        <Stat
          label="Collected this month"
          value={formatMoney(collections.collectedThisMonthMinor, 'Not computed')}
          tone={collections.collectedThisMonthMinor === null ? 'muted' : 'default'}
        />
        <Stat
          label="Promises to pay, this week"
          value={formatMoney(commitments.dueMinor)}
          hint={
            commitments.overdueCount > 0
              ? `${formatCount(commitments.overdueCount)} broken, ${formatMoney(commitments.overdueMinor)}`
              : `${formatCount(commitments.dueCount)} open`
          }
          tone={commitments.overdueCount > 0 ? 'warning' : 'default'}
        />
      </div>

      <h3 className="mt-5 mb-2 text-sm font-semibold text-brand-900">Aging</h3>
      <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm sm:grid-cols-6">
        {AGING_BUCKETS.map((bucket) => (
          <div key={bucket}>
            <dt className="text-xs text-ink-500">{AGING_BUCKET_META[bucket].label}</dt>
            <dd className="font-medium tabular-nums text-brand-900">
              {formatMoney(collections.buckets[bucket])}
            </dd>
          </div>
        ))}
      </dl>
    </SectionCard>
  )
}

/* ------------------------------------------------------------------------ *
 * Budget
 * ------------------------------------------------------------------------ */

function Budget({ budget }: { budget: WeeklyBudget }) {
  return (
    <SectionCard section="budget" description={`${budget.periodLabel}, accrual basis.`}>
      {!budget.hasBudget && (
        <Alert tone="info" className="mb-4" title="No budget imported for this month">
          Import one on the Budget page to see variances.
        </Alert>
      )}
      {!budget.actualsAvailable && (
        <Alert tone="warning" className="mb-4" title="Actuals are unavailable">
          {budget.unavailableReason ?? 'The QuickBooks P&L could not be read.'}
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Stat
          label="Revenue"
          value={formatMoney(budget.revenueActualMinor, 'Unavailable')}
          tone={budget.revenueActualMinor === null ? 'muted' : 'default'}
          hint={`Budget ${formatMoney(budget.revenueBudgetedMinor)} · variance ${formatMoney(budget.revenueVarianceMinor)}`}
        />
        <Stat
          label="Expenses"
          value={formatMoney(budget.expenseActualMinor, 'Unavailable')}
          tone={budget.expenseActualMinor === null ? 'muted' : 'default'}
          hint={`Budget ${formatMoney(budget.expenseBudgetedMinor)} · variance ${formatMoney(budget.expenseVarianceMinor)}`}
        />
      </div>

      {budget.worstLines.length > 0 && (
        <>
          <h3 className="mt-5 mb-2 text-sm font-semibold text-brand-900">Largest unfavourable lines</h3>
          <CompactTable>
              <thead>
                <tr>
                  <Th>Category</Th>
                  <Th className="text-right">Budget</Th>
                  <Th className="text-right">Actual</Th>
                  <Th className="text-right">Variance</Th>
                </tr>
              </thead>
              <tbody>
                {budget.worstLines.map((line) => (
                  <tr key={line.category}>
                    <Td>{line.category}</Td>
                    <Td className="text-right tabular-nums">{formatMoney(line.budgetedMinor)}</Td>
                    <Td className="text-right tabular-nums">{formatMoney(line.actualMinor)}</Td>
                    <Td className="text-right tabular-nums text-red-700">
                      {line.varianceBps === null ? '—' : formatBps(line.varianceBps)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </CompactTable>
        </>
      )}
    </SectionCard>
  )
}

/* ------------------------------------------------------------------------ *
 * Production
 * ------------------------------------------------------------------------ */

function Production({ production }: { production: WeeklyProduction }) {
  const { month, billing } = production

  return (
    <SectionCard
      section="production"
      description={
        month
          ? `Month to date from ${formatDate(month.periodStart)}${month.isPartial ? ', still open' : ''}.`
          : 'Month-to-date figures appear once the month has been computed.'
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Stat
          label="Billable hours"
          value={month ? formatHours(month.billableSeconds) : 'Not computed'}
          tone={month ? 'default' : 'muted'}
          hint={month ? `${formatHours(month.workedSeconds)} worked in total` : null}
        />
        <Stat
          label="Billed"
          value={month ? formatMoney(month.billedMinor) : 'Not computed'}
          tone={month ? 'default' : 'muted'}
        />
        <Stat
          label="Unbilled work (WIP)"
          value={billing ? formatMoney(billing.wipMinor) : 'No forecast run'}
          tone={billing ? 'default' : 'muted'}
          hint={
            billing
              ? `${formatMoney(billing.blockedMinor)} blocked by exceptions · run ${formatRelative(billing.generatedAt)}`
              : null
          }
        />
        <Stat
          label="Projected new AR"
          value={billing ? formatMoney(billing.projectedNewArMinor) : 'No forecast run'}
          tone={billing ? 'default' : 'muted'}
          hint={billing ? 'This billing cycle' : null}
        />
      </div>
    </SectionCard>
  )
}

/* ------------------------------------------------------------------------ *
 * Retainers
 * ------------------------------------------------------------------------ */

function Retainers({ retainers }: { retainers: WeeklyRetainers }) {
  const { may } = useSession()
  const [editing, setEditing] = useState(false)
  const met = retainers.progressBps !== null && retainers.progressBps >= 10_000

  return (
    <SectionCard
      section="retainers"
      description="Trust deposits this week, top-ups included, against the firm's weekly goal."
    >
      {retainers.goalMinor === null && !editing && (
        <Alert tone="info" className="mb-4" title="No weekly goal set">
          {may('budgets.manage')
            ? 'Set one below to track retainers against it.'
            : 'Finance has not set a weekly retainer goal yet.'}
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Stat
          label="Retainers this week"
          value={formatMoney(retainers.depositedMinor)}
          hint={`${formatCount(retainers.depositCount)} deposit${retainers.depositCount === 1 ? '' : 's'} from ${formatCount(retainers.clientCount)} client${retainers.clientCount === 1 ? '' : 's'}`}
        />
        <Stat
          label="Weekly goal"
          value={formatMoney(retainers.goalMinor, 'Not set')}
          tone={retainers.goalMinor === null ? 'muted' : 'default'}
          hint={
            retainers.remainingMinor === null
              ? null
              : met
                ? 'Goal met'
                : `${formatMoney(retainers.remainingMinor)} to go`
          }
        />
      </div>

      {retainers.progressBps !== null && retainers.goalMinor !== null && (
        <div className="mt-4">
          <Progress
            value={Math.min(retainers.depositedMinor, retainers.goalMinor)}
            max={retainers.goalMinor}
            tone={met ? 'positive' : 'brand'}
            label="Retainers against the weekly goal"
          />
          <p className="mt-1 text-xs text-ink-600">{formatBps(retainers.progressBps)} of goal</p>
        </div>
      )}

      {may('budgets.manage') &&
        (editing ? (
          <RetainerGoalForm goalMinor={retainers.goalMinor} onDone={() => setEditing(false)} />
        ) : (
          <Button size="sm" variant="ghost" className="mt-4" onClick={() => setEditing(true)}>
            {retainers.goalMinor === null ? 'Set weekly goal' : 'Change weekly goal'}
          </Button>
        ))}
    </SectionCard>
  )
}

/** Dollars on screen, minor units on the wire. Blank clears the goal. */
function RetainerGoalForm({ goalMinor, onDone }: { goalMinor: number | null; onDone: () => void }) {
  const queryClient = useQueryClient()
  const [dollars, setDollars] = useState(goalMinor === null ? '' : String(goalMinor / 100))
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (weeklyRetainerGoalMinor: number | null) =>
      apiFetch<RetainerGoalSettings>('/weekly/retainer-goal', {
        method: 'PATCH',
        body: JSON.stringify({ weeklyRetainerGoalMinor }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['weekly'] })
      onDone()
    },
    onError: (saveError: Error) =>
      setError(saveError instanceof ApiError ? saveError.message : 'The goal could not be saved.'),
  })

  const submit = () => {
    const trimmed = dollars.trim().replace(/[$,]/g, '')
    if (trimmed === '') return save.mutate(null)

    const minor = Math.round(Number(trimmed) * 100)
    if (!Number.isFinite(minor) || minor <= 0) {
      setError('Enter a dollar amount above zero, or leave it blank to clear the goal.')
      return
    }
    save.mutate(minor)
  }

  return (
    <form
      className="mt-4 flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <Field id="retainer-goal" label="Weekly retainer goal" hint="Dollars. Leave blank to clear.">
        <TextInput
          id="retainer-goal"
          inputMode="decimal"
          value={dollars}
          onChange={(event) => setDollars(event.target.value)}
        />
      </Field>
      <Button type="submit" size="sm" variant="accent" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : 'Save'}
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={onDone}>
        Cancel
      </Button>
      {error && (
        <Alert tone="error" className="w-full">
          {error}
        </Alert>
      )}
    </form>
  )
}

/* ------------------------------------------------------------------------ *
 * Trust
 * ------------------------------------------------------------------------ */

function Trust({ trust }: { trust: WeeklyTrust }) {
  const { reconciliation } = trust

  return (
    <SectionCard
      section="trust"
      description={`${formatCount(trust.matterCount)} matters hold or have held client money.`}
    >
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Held in trust" value={formatMoney(trust.heldMinor)} hint="Matters in credit" />
        <Stat
          label="Overdrawn"
          value={formatMoney(trust.overdrawnMinor)}
          tone={trust.overdrawnMatterCount > 0 ? 'warning' : 'default'}
          hint={`${formatCount(trust.overdrawnMatterCount)} matter${trust.overdrawnMatterCount === 1 ? '' : 's'} in deficit. Never netted against the held total.`}
        />
        <Stat
          label="Matters at risk"
          value={formatCount(trust.atRiskCount)}
          tone={trust.atRiskCount > 0 ? 'warning' : 'default'}
          hint={`${formatCount(trust.eventFlaggedCount)} with a hearing soon and a low balance`}
        />
        <Stat
          label="Unallocated"
          value={formatMoney(trust.unallocatedMinor)}
          hint={`Held for ${formatCount(trust.unallocatedClientCount)} client${trust.unallocatedClientCount === 1 ? '' : 's'} against no matter`}
        />
      </div>

      <p className="mt-4 text-sm text-ink-700">
        {reconciliation ? (
          <>
            Reconciled against Clio {formatRelative(reconciliation.checkedAt)}:{' '}
            {formatCount(reconciliation.reconciledCount)} of {formatCount(reconciliation.matterCount)}{' '}
            matters agree.{' '}
            {reconciliation.varianceCount > 0 && (
              <Badge tone="warning">
                {formatCount(reconciliation.varianceCount)} disagree ·{' '}
                {formatMoney(reconciliation.absoluteVarianceMinor)} apart
              </Badge>
            )}{' '}
            {reconciliation.unavailableCount > 0 && (
              <Badge tone="neutral">{formatCount(reconciliation.unavailableCount)} not compared</Badge>
            )}
          </>
        ) : (
          'No reconciliation against Clio has run yet.'
        )}
      </p>
      <p className="mt-1 text-xs text-ink-500">
        A two-way check (our ledger against Clio). Comparing to the bank statement is not built yet.
      </p>
    </SectionCard>
  )
}

/* ------------------------------------------------------------------------ *
 * Not built
 * ------------------------------------------------------------------------ */

function NotBuilt() {
  return (
    <section aria-labelledby="not-built">
      <h2 id="not-built" className="mb-3 font-display text-lg font-semibold text-brand-900">
        Not built yet
      </h2>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {WEEKLY_NOT_BUILT.map((item) => (
          <div
            key={item.label}
            className="rounded-lg border border-dashed border-ink-300 bg-white px-4 py-3"
          >
            <p className="text-[11px] font-semibold tracking-[0.12em] text-ink-500 uppercase">
              {item.label}
            </p>
            <p className="mt-1 text-sm text-ink-600">{item.reason}</p>
          </div>
        ))}
      </div>
    </section>
  )
}
